import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbDateStruct, NgbDatepickerNavigateEvent, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { FormlyModule } from '@ngx-formly/core';

import { FormlyFieldBaseComponent } from '../formly-field-base.component';

@Component({
  selector: 'app-formly-date',
  templateUrl: './formly-date.component.html',
  styleUrl: './formly-date.component.scss',
  imports: [NgbInputDatepicker, FormsModule, ReactiveFormsModule, FormlyModule],
})
export class FormlyDateComponent extends FormlyFieldBaseComponent {
  private readonly today = new Date();

  // Where the calendar popup renders. Inline (the default) grows the surrounding layout,
  // which makes a MODAL scroll when the picker opens - a field inside a modal passes
  // props.floatDropdown to float the popup over the page instead (R15, 28 Aug notes:
  // "the date pickers make the screen scroll"). ng-bootstrap's own ngb-dp-body styling
  // keeps a body-appended popup above the modal, so this is its sanctioned modal shape -
  // unlike ng-select, which must NOT be appended to body (see formly-select).
  protected get container(): 'body' | null {
    return this.props.floatDropdown ? 'body' : null;
  }

  // The year the +/- range is centred on. Starts at the current year and re-centres on
  // whatever year the user navigates/selects, so they can keep moving outwards indefinitely. The
  // window is deliberately kept small so the (native) year dropdown stays short — re-centring, not a
  // huge window, provides the reach. A field can widen/narrow the span via
  // props.yearsBefore / props.yearsAfter.
  private centreYear = this.today.getFullYear();

  private get yearsBefore(): number {
    return this.props.yearsBefore ?? 15;
  }

  private get yearsAfter(): number {
    return this.props.yearsAfter ?? 15;
  }

  /** The year of the value already stored in the field, if it holds a usable date. */
  private get valueYear(): number | undefined {
    const value = this.formControl?.value;
    if (!value) {
      return undefined;
    }
    const year = new Date(value).getFullYear();
    return isNaN(year) ? undefined : year;
  }

  // ⚠️ min/max ALWAYS stretch to include the value the field already holds. ngb applies these as
  // VALIDATORS as well as limiting the picker, so a stored date outside the window makes the control
  // invalid — and on a form built by formlyService.table() that error is invisible (the table strips
  // the wrappers), so Save silently does nothing. That is what broke saving new currency rates: one
  // legacy rate dated 01/01/2010 fell outside 2026-15. The window is only meant to keep the year
  // dropdown short; it must never reject data that is already in the database.
  get minDate(): NgbDateStruct {
    const windowStart = this.centreYear - this.yearsBefore;
    return { year: Math.min(windowStart, this.valueYear ?? windowStart), month: 1, day: 1 };
  }

  get maxDate(): NgbDateStruct {
    const windowEnd = this.centreYear + this.yearsAfter;
    return { year: Math.max(windowEnd, this.valueYear ?? windowEnd), month: 12, day: 31 };
  }

  // Only steer the initial view when the field is EMPTY (open on the current month/year). When the
  // field already has a value, return undefined so the datepicker opens on that value rather than
  // being forced to today.
  get startDate(): NgbDateStruct | undefined {
    return this.formControl?.value
      ? undefined
      : { year: this.today.getFullYear(), month: this.today.getMonth() + 1, day: 1 };
  }

  // Re-centre the +/- range on the year the user navigated to (opening on a valued field fires this
  // too, so the range also recentres on the field's own year).
  navigate(event: NgbDatepickerNavigateEvent) {
    if (event?.next?.year && event.next.year !== this.centreYear) {
      this.centreYear = event.next.year;
    }
  }

  change() {
    if (this.props.change) {
      this.props.change(this.field, { target: { value: this.field.model[this.key] } });
    }
  }
}
